<template>
  <v-card v-if="ticket && statuses && members && localComment">
    <v-card-title>
      <v-avatar size=48>
        <v-icon color="primary" large>forum</v-icon>
      </v-avatar>
      <span class="subheading text-uppercase pl-2">
        Add a comment
      </span>
      <v-flex class="text-xs-right">
        <v-avatar size=24>
          <v-icon v-show="lowPriority"
            @click="setPriority('medium')"
            color="#ff6600"
          >star_outline</v-icon>
          <v-icon v-show="mediumPriority"
            @click="setPriority('high')"
            color="#ff6600"
          >star_half</v-icon>
          <v-icon v-show="highPriority"
            @click="setPriority('low')"
            color="#ff6600"
          >star</v-icon>
        </v-avatar>
      </v-flex>
    </v-card-title>

    <v-card-text>
      <v-form
        ref="form"
        lazy-validation
        >
        <v-layout row>
          <v-flex xs12 mb-3>
            <vueTrix v-model="localComment.message"
              placeholder="Type and format your comment"
              autofocus
              @trix-attachment-add="trixAddAttachment"
              @trix-file-accept="trixFileAccept"
              />
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs12 md6 xl4>
            <v-combobox
              v-model="localComment.tag_list"
              label="Tags"
              chips
              multiple
              deletable-chips
              small-chips
              prepend-icon="bookmarks"
            ></v-combobox>
            <v-select
              v-show="boards.length > 0"
              v-model="boardIds"
              :items="boards.map((brd) => {return {id: brd.id, name: brd.attributes.name}})"
              prepend-icon="view_carousel"
              item-text="name"
              item-value="id"
              label="Place on board(s)"
              chips
              multiple
              deletable-chips
              small-chips
            ></v-select>

            <v-select
              label="Status"
              name="status"
              v-model="localComment.status_id"
              :items="statuses.map(st => st.attributes)"
              item-text="name"
              item-value="id"
              solo
              prepend-icon="view_column"
              :rules="[rules.required]"
              >
              <template v-slot:item="{ item }">
                <v-chip :color="item.color" disabled small class="ma-0 pa-0 mr-3"></v-chip>
                {{item.name}}
              </template>
              <template v-slot:selection="{ item }">
                <v-chip :color="item.color" disabled small class="ma-0 pa-0 mr-3"></v-chip>
                {{item.name}}
              </template>
            </v-select>

            <v-select
              label="Assignment"
              name="assignment"
              v-model="localComment.assignee_id"
              :items="membershipChoices()"
              item-text="name"
              item-value="id"
              solo
              prepend-icon="person"
            >
              <template v-slot:item="{ item }">
                <v-avatar size=24 class="pr-3">
                  <img :src="assigneeAvatarUrl(item)" alt="Avatar" v-if="assigneeAvatarUrl(item)">
                  <v-icon v-else>person_outline</v-icon>
                </v-avatar>
                {{item.name}}
              </template>
              <template v-slot:selection="{ item }">
                <v-avatar size=24 class="pr-3">
                  <img :src="assigneeAvatarUrl(item)" alt="Avatar" v-if="assigneeAvatarUrl(item)">
                  <v-icon v-else>person_outline</v-icon>
                </v-avatar>
                {{item.name}}
              </template>
            </v-select>
          </v-flex>
          <v-flex xs12 md6 xl8 :style="'background: url(' + logoUrl + ') no-repeat center;'" pl-3>
            <vue-dropzone
              ref='attachmentDropzone'
              id='dropzone'
              style="opacity: 0.9;"
              :options='dropzoneOptions'
              @vdropzone-queue-complete="dzQueueComplete"
            ></vue-dropzone>
          </v-flex>
        </v-layout>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn flat color="primary" @click="cancel()">Cancel</v-btn>
      <v-btn color="primary" @click="saveComment()" :loading="busy">Add Comment</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
  import { EventBus } from '@/utilities/event-bus'
  import authentication from '@/utilities/authentication'

  import vueDropzone from 'vue2-dropzone'
  import 'vue2-dropzone/dist/vue2Dropzone.min.css'

  import vueTrix from 'vue-trix'

  import commentService from '@/services/comment'
  import placementService from '@/services/placement'
  import watchService from '@/services/watch'

  export default {
    name: 'comment-new',

    props: ['ticket', 'statuses', 'boards', 'members'],

    data () {
      return {
        auth: authentication,
        localComment: null,
        tmpComment: null, // we use this to re-fetch the comment we just saved to also get the file links
        boardIds: [],
        dropzoneOptions: {
          url: 'unknown', // Set this once the comment is saved
          method: 'put',
          paramName: 'comment[attachments][]',
          thumbnailWidth: 150,
          maxFilesize: 50.0,
          // maxFiles: 10,
          // acceptedFiles: 'image/*', // we dont want to restrivt uploads on tickets
          headers: {
            'X-User-Email': authentication.username(),
            'X-User-Token': authentication.token()
          },
          dictDefaultMessage: 'Attach files to your comment',
          // we don't want DZ to auto-upload the files, we need to make sure it saves first
          autoProcessQueue: false,
          addRemoveLinks: true
        },
        busy: false,
        rules: {
          required: value => !!value || 'Required'
        }
      }
    },

    components: {
      vueDropzone,
      vueTrix
    },

    computed: {
      logoUrl () {
        return require('@/assets/largeicon.png')
      },

      lowPriority () {
        return (this.localComment.priority == 'low') || (this.localComment.priority == null)
      },

      mediumPriority () {
        return (this.localComment.priority == 'medium')
      },

      highPriority () {
        return (this.localComment.priority == 'high')
      }
    },

    watch: {
      ticket: {
        handler (val) {
          if (this.ticket) {
            this.initComment()
          }
        }
      }
    },

    mounted () {
      this.initComment()
      // make sure our boards are sorted
      this.boards.sort((a, b) => { return a.attributes.name.toLowerCase().localeCompare(b.attributes.name.toLowerCase()) })
    },

    methods: {
      initComment () {
        this.localComment = Object.assign({}, {
          status_id: this.ticket.relationships.status.data && parseInt(this.ticket.relationships.status.data.id),
          assignee_id: this.ticket.relationships.assignee.data && parseInt(this.ticket.relationships.assignee.data.id),
          tag_list: this.ticket.attributes.tag_list,
          priority: this.ticket.attributes.priority
        })
        this.boardIds = this.ticket.relationships.boards.data.map(brd => brd.id)
      },

      cancel () {
        this.$refs.form.reset()
        this.$nextTick(() => {
          this.initComment()
          EventBus.$emit('comment-created')
        })
      },

      saveComment () {
        if (this.$refs.form.validate()) {
          this.busy = true
          commentService.ticketCreate(
            async (data) => {
              // we need to set the status id or the form will temporarily be invalid
              data.attributes.status_id = (this.ticket.relationships.status.data && parseInt(this.ticket.relationships.status.data.id))
              this.localComment = Object.assign({}, data.attributes)

              this.$refs.attachmentDropzone.setOption('url', process.env.API_URL + data.links.update_uri + '.json')

              // if we messed with the boards, we need to update placements
              await this.updateTicketPlacements()

              // if a user is assigned, make sure they have a watch
              if (data.relationships.assignee.data) {
                this.setWatch(data.relationships.assignee.data.id)
              }

              if (this.$refs.attachmentDropzone.getQueuedFiles().length > 0) {
                this.$refs.attachmentDropzone.processQueue()
              } else {
                // no files queued, so just pretend we are done
                await this.dzQueueComplete()
              }
            },
            (err) => {
              this.busy = false
              console.log(err)
            },
            this.ticket.id,
            this.localComment
          )
        }
      },

      dzQueueComplete (file, xhr, formData) {
        this.$refs.attachmentDropzone.removeAllFiles()
        // NOTE: there is a catch here: when we save the comment we get the links to the OLD images. We need to reload the comment once the new image is saved, so we
        // can send that back and the caller does not have to deal with it.
        return commentService.ticketShow(
          (data) => {
            this.busy = false
            this.$refs.form.reset()
            this.$nextTick(() => {
              this.initComment()
              EventBus.$emit('comment-created', data)
            })
          },
          (err) => {
            console.log(err)
          },
          this.ticket.id,
          this.localComment.id
        )
      },

      trixFileAccept () {
        if (!/image\/*/.test(event.file.type)) {
          event.preventDefault()
          alert('Only image files are accepted')
        }
      },

      trixAddAttachment (event) {
        if (event.attachment && event.attachment.file) {
          let reader = new FileReader()
          reader.onload = () => {
            let attributes = {
              src: reader.result,
              url: reader.result,
              href: ''
            }
            event.attachment.setAttributes(attributes)
          }
          reader.readAsDataURL(event.attachment.file)
        }
      },

      async updateTicketPlacements () {
        // get all the placements for this ticket
        let placements = []
        await placementService.ticketIndex(
          (data) => {
            placements = data
          },
          (err) => {
            console.log(err)
          },
          this.ticket.id
        )

        // remove placements we no longer want
        await Promise.all(placements.map(async (plc) => {
          if (!this.boardIds.find(id => parseInt(id) === parseInt(plc.relationships.board.data.id))) {
            // the placement is not on the list of ones we want
            return placementService.ticketDelete(
              () => {
                // placement removed
              },
              (err) => {
                console.log(err)
              },
              this.ticket.id, plc.id
            )
          }
        }))

        // add placements as required
        await Promise.all(this.boardIds.map(async (brdId) => {
          // do we have a placement for it?
          if (!placements.find(pl => parseInt(pl.relationships.board.data.id) === parseInt(brdId))) {
            return placementService.ticketCreate(
              () => {
                // placement added
              },
              (err) => {
                console.log(err)
              },
              this.ticket.id,
              // -1 adds it to the bottom of the list
              {board_id: brdId, position: -1}
            )
          }
        }))
      },

      setWatch (assigneeId) {
        watchService.ticketCreate(
          (data) => {
            // nothing to do here, the modal will be cleared in a moment
          },
          (err) => {
            console.log(err)
          },
          this.ticket.id,
          {user_id: assigneeId}
        )
      },

      assigneeAvatarUrl (assignee) {
        if (assignee && assignee.avatar_uri) {
          return (process.env.API_URL + assignee.avatar_uri)
        } else {
          return null
        }
      },

      membershipChoices () {
        let choices = this.members.map((mem) => Object.assign({}, mem.attributes, mem.relationships.user.data, mem.links))
        choices.map((m) => {
          m.id = parseInt(m.id)
          return m
        })
        choices.unshift({name: 'None', id: null})
        return choices
      },

      setPriority (level) {
        this.localComment.priority = level
      }
    }
  }
</script>

<style lang="scss">
	trix-editor {
		height: 250px !important;
		max-height: 250px !important;
	 	overflow-y: auto !important;
	}
</style>



// WEBPACK FOOTER //
// src/components/comment/_new.vue